import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, MessageCircle, User } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { useSafeTwilioConversations } from '../../../contexts/twilioConversationContext.shared';
import logo from '../../../assets/logo.png';
import {
  emptyHistoryBackPath,
  loginReturnFromPath,
} from '../../../utils/publicSessionRoutes';
import './NativeHeader.css';

interface NativeHeaderProps {
  /** Affiche la flèche retour (défaut : true si canGoBack) */
  showBack?: boolean;
  /** Override le comportement retour */
  onBack?: () => void;
  /** Route parente explicite (prioritaire sur navigate(-1)) */
  backPath?: string;
  /** Remplace le logo par un titre texte */
  title?: string;
  /** Affiche le logo Rintintin au centre (défaut : true si pas de titre) */
  showLogo?: boolean;
  /** Action personnalisée à droite (ex. toggle carte recherche) */
  rightSlot?: React.ReactNode;
  /** Affiche l'icône compte en haut à droite (défaut : true) */
  showAccount?: boolean;
  /** Affiche l'icône messagerie (défaut : true) */
  showMessaging?: boolean;
  /** Header plus bas (ex. /recherche) */
  compact?: boolean;
}

/**
 * Header Capacitor-only — ← | Logo/Titre | Compte
 * Inspiré Planity : sobre, pas de mega-menu.
 */
const NativeHeader: React.FC<NativeHeaderProps> = ({
  showBack,
  onBack,
  backPath,
  title,
  showLogo = true,
  rightSlot,
  showAccount = true,
  showMessaging = true,
  compact = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('native');
  const { isAuthenticated } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const messagingVisible = showMessaging && isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
  const twilioContext = useSafeTwilioConversations();

  const unreadMessages = useMemo(() => {
    if (!twilioContext?.conversations) return 0;
    return twilioContext.conversations.reduce(
      (total, conv) => total + (conv.unreadMessagesCount || 0),
      0,
    );
  }, [twilioContext]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      const fallback = emptyHistoryBackPath(location.pathname);
      if (fallback) navigate(fallback);
    }
  };

  const openedSignatureWithoutHistory =
    emptyHistoryBackPath(location.pathname) === null && window.history.length <= 1;
  const canGoBack = (showBack ?? window.history.length > 1) && !openedSignatureWithoutHistory;
  const isDiscoveryHeader = !title && !showLogo && !!rightSlot && !showAccount && !messagingVisible;

  return (
    <header
      className={`native-header${isDiscoveryHeader ? ' native-header--discovery' : ''}${
        compact ? ' native-header--compact' : ''
      }`}
    >
      <div className="native-header__left">
        {canGoBack && (
          <button
            className="native-header__back"
            onClick={handleBack}
            aria-label={t('header.back')}
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
        )}
      </div>

      <div className="native-header__center">
        {title ? (
          <span className="native-header__title">{title}</span>
        ) : showLogo ? (
          <button
            type="button"
            className="native-header__logo"
            onClick={() => navigate('/')}
            aria-label={t('header.homeAria')}
          >
            <img src={logo} alt="Rintintin" className="native-header__logo-img" />
          </button>
        ) : null}
      </div>

      <div className="native-header__right">
        {rightSlot}
        {messagingVisible && (
          <button
            type="button"
            className="native-header__messaging"
            onClick={() =>
              navigate(isAuthenticated ? '/messaging' : '/login', {
                state: isAuthenticated ? undefined : { from: '/messaging' },
              })
            }
            aria-label={
              unreadMessages > 0
                ? t('header.messagesUnread', { count: unreadMessages })
                : t('header.messages')
            }
          >
            <MessageCircle size={20} strokeWidth={2} />
            {unreadMessages > 0 && (
              <span className="native-header__badge">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            )}
          </button>
        )}
        {showAccount && (
          <button
            className="native-header__account"
            onClick={() =>
              navigate(isAuthenticated ? '/profile' : '/login', {
                state: isAuthenticated
                  ? undefined
                  : { from: loginReturnFromPath(location.pathname, location.search) },
              })
            }
            aria-label={isAuthenticated ? t('header.account') : t('header.signIn')}
          >
            <User size={20} strokeWidth={2} />
          </button>
        )}
      </div>
    </header>
  );
};

export default NativeHeader;
